import BetSlipOdd from '@core/models/base/BetSlipOdd';

const localStorageKey = 'betBuilder::history';

export function getBetBuilderHistoryService(
    keepMaxValues = 3,
    ttlHours = 12,
) {
    const ttlMs = ttlHours * 60 * 60 * 1_000;

    function recordBetBuilderEntryForEvent(
        key: string,
        eventStartTime: Date,
        oddUuids: BetSlipOdd['uuid'][],
    ): void {
        const createdAt = new Date();
        const history = getBetBuilderHistory();

        history[key] = {
            createdAt,
            eventStartTime,
            oddUuids,
        };

        const updatedHistory = selectValidEntries(history);

        setBetBuilderHistory(updatedHistory);
    }

    function getBetBuilderOddUuidsForEvent(key: string): BetSlipOdd['uuid'][] {
        const history = getAndCleanupBetBuilderHistory();

        return history[key]?.oddUuids ?? [];
    }

    function removeBetBuilderOddUuidsForEvent(key: string): void {
        const history = getAndCleanupBetBuilderHistory();

        delete history[key];

        setBetBuilderHistory(history);
    }

    function getBetBuilderHistory(): BetBuilderHistory {
        const history = localStorage.getItem(localStorageKey);

        if (!history) {
            return {};
        }

        const parsedHistory = JSON.parse(history);

        return parseBetBuilderHistory(parsedHistory);
    }

    function getAndCleanupBetBuilderHistory(): BetBuilderHistory {
        const history = getBetBuilderHistory();
        const validHistory = selectValidEntries(history);
        setBetBuilderHistory(validHistory);
        return validHistory;
    }

    function selectValidEntries(history: BetBuilderHistory): BetBuilderHistory {
        return pickMostRecentValidEntries(history, ttlMs, keepMaxValues);
    }

    function setBetBuilderHistory(history: BetBuilderHistory): void {
        const serializedHistory = serializeBetBuilderHistory(history);
        localStorage.setItem(localStorageKey, JSON.stringify(serializedHistory));
    }

    function parseBetBuilderHistory(dto: BetBuilderHistoryDto): BetBuilderHistory {
        return Object.entries(dto).reduce((acc, [eventId, entry]) => {
            const { createdAt, eventStartTime, oddUuids } = entry;
            acc[eventId] = {
                createdAt: new Date(createdAt),
                eventStartTime: new Date(eventStartTime),
                oddUuids,
            };
            return acc;
        }, {} as BetBuilderHistory);
    }

    function serializeBetBuilderHistory(history: BetBuilderHistory): BetBuilderHistoryDto {
        return Object.entries(history).reduce((acc, [eventId, entry]) => {
            const { createdAt, eventStartTime, oddUuids } = entry;
            acc[eventId] = {
                createdAt: createdAt.toISOString(),
                eventStartTime: eventStartTime.toISOString(),
                oddUuids,
            };
            return acc;
        }, {} as BetBuilderHistoryDto);
    }

    return {
        recordBetBuilderEntryForEvent,
        getBetBuilderOddUuidsForEvent,
        removeBetBuilderOddUuidsForEvent,
    };
}

export function pickMostRecentValidEntries(
    history: BetBuilderHistory,
    ttlMs: number,
    keepMaxValues: number,
): BetBuilderHistory {
    const now = new Date();
    const entries = Object.entries(history);

    const validEntries = entries.filter(([, entry]) => {
        const { createdAt, eventStartTime } = entry;
        const isExpired = eventStartTime.getTime() < now.getTime()
            || now.getTime() - createdAt.getTime() > ttlMs;
        return !isExpired;
    });

    const sortedEntries = validEntries.sort(([, entryA], [, entryB]) => {
        const { createdAt: createdAtA } = entryA;
        const { createdAt: createdAtB } = entryB;
        return createdAtA.getTime() - createdAtB.getTime();
    });

    const mostRecentEntries = sortedEntries.slice(-keepMaxValues);

    return mostRecentEntries.reduce((acc, [eventId, entry]) => {
        acc[eventId] = entry;
        return acc;
    }, {} as BetBuilderHistory);
}

export const betBuilderHistoryService = getBetBuilderHistoryService();

type BetBuilderOddHistoryEntry = {
    createdAt: Date;
    eventStartTime: Date;
    oddUuids: BetSlipOdd['uuid'][];
};

type BetBuilderHistory = Record<string, BetBuilderOddHistoryEntry>;

type BetBuilderOddHistoryEntryDto = {
    createdAt: string;
    eventStartTime: string;
    oddUuids: string[];
};

type BetBuilderHistoryDto = Record<string, BetBuilderOddHistoryEntryDto>;
